import React, { useState } from 'react';
import {
  Cog6ToothIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export interface VblForecastCISettings {
  confidenceLevel: number;
  useSurvivalAdaptation: boolean;
  survivalAdaptationStrength: number;
  earlyStageThreshold: number;
}

interface VblForecastSettingsSidebarProps {
  settings: VblForecastCISettings;
  onSettingsChange: (settings: VblForecastCISettings) => void;
  onApply: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const VblForecastSettingsSidebar: React.FC<VblForecastSettingsSidebarProps> = ({
  settings,
  onSettingsChange,
  onApply,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const [customConfidenceLevel, setCustomConfidenceLevel] = useState(Math.round(settings.confidenceLevel * 1000) / 10)
  const [validCustom, setValidCustom] = useState(true);
  const handleChange = (field: keyof VblForecastCISettings, value: string | number | boolean) => {
    if (settings[field] !== value) {
      onSettingsChange({
        ...settings,
        [field]: value
      });
      onApply();
    }
  };

  const handleConfidenceLevelChange = (level: number) => {
    if (customConfidenceLevel !== level) {
      setCustomConfidenceLevel(level);
    }
    setValidCustom(true);
    handleChange('confidenceLevel', level / 100); // Convert percentage to decimal
  };

  if (isCollapsed) {
    return (
      <div className="bg-white border-r border-gray-200 h-full flex flex-col w-12">
        <div className="p-3 border-b border-gray-200">
          <button
            onClick={onToggleCollapse}
            className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            title="Expand VBL settings"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center">
          <Cog6ToothIcon className="h-6 w-6 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Overlay */}
      <div className="lg:hidden fixed inset-0 z-50 flex">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onToggleCollapse} />
        <div className="relative flex flex-col w-full max-w-xs bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              onClick={onToggleCollapse}
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Close settings</span>
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="h-0 flex-1 pt-5 pb-4 overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">VBL Prediction Settings</h3>
              </div>
            </div>
            <div className="px-6 py-4 space-y-4">
              {/* Mobile Content */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Confidence Level</h4>
                <div className="flex items-center gap-3">
                  {/* Preset buttons group */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleConfidenceLevelChange(70)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${Math.round(settings.confidenceLevel * 100) === 70
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      70%
                    </button>
                    <button
                      onClick={() => handleConfidenceLevelChange(90)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${Math.round(settings.confidenceLevel * 100) === 90
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      90%
                    </button>
                    <button
                      onClick={() => handleConfidenceLevelChange(95)}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${Math.round(settings.confidenceLevel * 100) === 95
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                      95%
                    </button>
                  </div>

                  {/* Separator */}
                  <div className="h-8 w-px bg-gray-300" />

                  {/* Custom input group */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Custom:</label>
                    <input
                      type="number"
                      min="50"
                      max="99.9"
                      step="0.1"
                      value={customConfidenceLevel}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        setCustomConfidenceLevel(value);
                        setValidCustom(false)
                        if (!isNaN(value) && value >= 50 && value <= 99.9) {
                          setValidCustom(true)
                        }
                      }}
                      onBlur={(e) => {
                        const value = parseFloat(e.target.value);
                        if (validCustom) {
                          handleConfidenceLevelChange(value);
                        }
                      }}
                      className={validCustom ? "w-20 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" : "w-20 px-3 py-2 text-sm border border-red-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"}
                      placeholder="90"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                </div>
              </div>

              {/* Recalculate Button */}
              <div className="pt-4">
                <button
                  onClick={onApply}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  Recalculate
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Updates forecast predictions with new confidence interval settings
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex bg-white border-r border-gray-200 h-full flex-col w-80">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Cog6ToothIcon className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">VBL Prediction Settings</h3>
            </div>
            <button
              onClick={onToggleCollapse}
              className="p-1 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              title="Close VBL settings"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          </div>

          <p className="text-sm text-gray-600">
            Configure confidence intervals for VBL predictions
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Confidence Level */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <AdjustmentsHorizontalIcon className="h-4 w-4 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-700">Confidence Level</h4>
            </div>

            <div className="space-y-4">
              {/* Preset buttons */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => handleConfidenceLevelChange(70)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${Math.round(settings.confidenceLevel * 100) === 70
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  70%
                </button>
                <button
                  onClick={() => handleConfidenceLevelChange(90)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${Math.round(settings.confidenceLevel * 100) === 90
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  90%
                </button>
                <button
                  onClick={() => handleConfidenceLevelChange(95)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${Math.round(settings.confidenceLevel * 100) === 95
                    ? 'bg-blue-600 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  95%
                </button>
              </div>

              {/* Custom input */}
              <div className="space-y-2">
                <label className="text-sm text-gray-600">Custom Confidence Level:</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="50"
                    max="99.9"
                    step="0.1"
                    value={customConfidenceLevel}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setCustomConfidenceLevel(value);
                      setValidCustom(false)
                      if (!isNaN(value) && value >= 50 && value <= 99.9) {
                        setValidCustom(true)
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseFloat(e.target.value);
                      if (validCustom) {
                        handleConfidenceLevelChange(value);
                      }
                    }}
                    className={validCustom ? "flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500" : "flex-1 px-3 py-2 text-sm border border-red-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"}
                    placeholder="90.0"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-500">
                  Enter a value between 50% and 99.9%
                </p>
              </div>
            </div>
          </div>

          {/* Current Settings Summary */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h5 className="text-sm font-medium text-gray-700 mb-2">Current Settings</h5>
            <div className="text-sm text-gray-600 space-y-1">
              <div>Confidence Level: <span className="font-medium">{Math.round(settings.confidenceLevel * 1000) / 10}%</span></div>
              <div>Survival Adaptation: <span className="font-medium">{settings.useSurvivalAdaptation ? 'Enabled' : 'Disabled'}</span></div>
            </div>
          </div>

          {/* Recalculate Button */}
          <div className="space-y-3">
            <button
              onClick={onApply}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Recalculate
            </button>
            <p className="text-xs text-gray-500 text-center">
              Updates all forecast predictions and confidence intervals with the new settings
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default VblForecastSettingsSidebar;